import { ReservationGroupResult } from '@mews-distributor/data-access';
import { DateTimeUtc, DateTimeUtils, RecaptchaAction } from '@mews/framework';
import { createAction } from '@mews/framework/redux';
import {
    getAvailabilityBlockIdFromQuery,
    getLanguageCode,
    getReservationGroupIdFromQuery,
} from '../../modules/app/selectors';
import { getSelectedHotelConfigurationId } from '../../modules/configuration/selectors';
import { getReservationsList } from '../../modules/entities/reservations/selectors';
import { getEnterpriseServiceId, hasEnterpriseDayUseServices } from '../../modules/entities/services/selectors';
import { getPromotedServiceReservations } from '../../modules/promotedServiceReservations/selectors';
import {
    CREATE_RESERVATION_GROUP_FAILED,
    CREATE_RESERVATION_GROUP_STARTED,
    CREATE_RESERVATION_GROUP_SUCCEEDED,
    FETCH_RESERVATION_GROUP_FAILED,
    FETCH_RESERVATION_GROUP_STARTED,
    FETCH_RESERVATION_GROUP_SUCCEEDED,
} from '../../modules/reservationGroups/actionTypes';
import { getSelectedHotelId } from '../../modules/resourceOrder/selectors';
import { ROOM_MAX_AVAILABLE_COUNT_CHANGED } from '../../modules/serviceAvailability/actionTypes';
import { isValidAvailabilityBlock } from '../../selectors/views/availabilityBlocks';
import { getSelectedEnterpriseTimezone } from '../../selectors/views/selectedEnterprise/selectedEnterprise';
import { isRecaptchaVerificationEnabled } from '../../services/featureFlagService/featureFlags/recaptcha';
import {
    ApplicationState,
    DistributorPromiseThunkAction,
    DistributorThunkDispatch,
    ThunkExtraArgument,
} from '../../types';
import { toUtc } from '../../utils/dates';
import {
    getBookerEmail,
    getBookerFirstName,
    getBookerLastName,
    getBookerPhone,
    getCustomerAddress,
    getCustomerCity,
    getCustomerEmail,
    getCustomerFirstName,
    getCustomerLastName,
    getCustomerNationalityCode,
    getCustomerNotes,
    getCustomerPhone,
    getCustomerPostalCode,
    getSendMarketingEmails,
    isBookerEnabled,
} from '../../views/checkout/data/selectors';
import { getExceedingReservationsData } from '../apiUtils/getExceedingReservationsData';
import { logSentryError } from '../../sentry/sentry';

interface exceedingReservationsCountType {
    availabilityHash: string;
    roomCategoryId: string;
    requestedCount: number;
    exceedingCount: number;
    serviceAvailabilityHash: string;
    startUtc: DateTimeUtc;
    endUtc: DateTimeUtc;
    startTimeUtc?: string;
    endTimeUtc?: string;
}

const updateAvailabilityData =
    (exceedingReservationsCounts: exceedingReservationsCountType[], isDayUse: boolean, timezone: string) =>
    (dispatch: DistributorThunkDispatch, _: () => ApplicationState) => {
        exceedingReservationsCounts.forEach(
            ({
                availabilityHash,
                roomCategoryId,
                requestedCount,
                exceedingCount,
                serviceAvailabilityHash,
                startUtc,
                endUtc,
                startTimeUtc,
                endTimeUtc,
            }) => {
                const maxAvailableCount = requestedCount - exceedingCount;
                const availableCount = maxAvailableCount - requestedCount;

                // Only for hourly services
                const startTime =
                    startTimeUtc != null ? DateTimeUtils.dateTimeUtcStringToTime(startTimeUtc, timezone) : undefined;
                const endTime =
                    endTimeUtc != null ? DateTimeUtils.dateTimeUtcStringToTime(endTimeUtc, timezone) : undefined;

                return dispatch(
                    createAction(ROOM_MAX_AVAILABLE_COUNT_CHANGED, {
                        availabilityHash,
                        serviceAvailabilityHash,
                        startUtc,
                        endUtc,
                        maxAvailableCount,
                        availableCount,
                        categoryId: roomCategoryId,
                        isDayUse,
                        timezone,
                        startTime,
                        endTime,
                    })
                );
            }
        );
    };

export function createReservationGroup({ enterpriseId: hotelId, creditCardData = null }: $TSFixMe = {}) {
    return async (
        dispatch: DistributorThunkDispatch,
        getState: () => ApplicationState,
        {
            apiService,
            appClockService,
            environmentService,
            featureFlagService,
            gainsightService,
            loggerService,
            recaptchaService,
        }: ThunkExtraArgument
    ) => {
        const state = getState();
        const configurationId = getSelectedHotelConfigurationId(state);
        const languageCode = getLanguageCode(state);
        const reservations = getReservationsList(state);
        const bookerEnabled = isBookerEnabled(state);
        const availabilityBlockId = getAvailabilityBlockIdFromQuery(state);
        const nowUtc = appClockService.getAppNowUtc();
        const serviceId = getEnterpriseServiceId(state, { enterpriseId: hotelId });

        const validAvailabilityBlockId =
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            availabilityBlockId && isValidAvailabilityBlock(state, { availabilityBlockId, nowUtc })
                ? availabilityBlockId
                : undefined;
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        const timezone = getSelectedEnterpriseTimezone(state, {}) || 'Europe/London';
        const notes = getCustomerNotes(state);
        const finalReservations = reservations.map(
            ({
                orderId: identifier,
                roomId: roomCategoryId,
                startDate,
                endDate,
                voucherCode,
                adultCount,
                childCount,
                occupancyData,
                productIds,
                rateId,
                startTimeUtc,
                endTimeUtc,
            }) => ({
                identifier,
                roomCategoryId,
                startUtc: startTimeUtc ?? toUtc(startDate, timezone),
                endUtc: endTimeUtc ?? toUtc(endDate, timezone),
                voucherCode,
                adultCount,
                childCount,
                occupancyData,
                productIds,
                rateId,
                notes,
                availabilityBlockId: validAvailabilityBlockId,
            })
        );

        const promotedServiceReservations = timezone != null ? getPromotedServiceReservations(state, { timezone }) : [];
        const finalPromotedServiceReservations =
            timezone != null
                ? promotedServiceReservations.map(
                      ({ orderId, serviceId: promotedServiceAssignmentId, startUtc, endUtc, reservationId }) => ({
                          parentReservationIdentifier: orderId,
                          promotedServiceAssignmentId,
                          startUtc,
                          endUtc,
                          identifier: reservationId,
                      })
                  )
                : [];

        dispatch(createAction(CREATE_RESERVATION_GROUP_STARTED));
        if (timezone == null) {
            const error = new TypeError('enterprises timezone is not defined');
            dispatch(createAction(CREATE_RESERVATION_GROUP_FAILED, { error }));
            throw error;
        }

        let recaptchaVerificationToken: string | null = null;

        try {
            recaptchaVerificationToken = await recaptchaService.execute(RecaptchaAction.CheckOut);
        } catch (e) {
            const isRecaptchaEnabled = isRecaptchaVerificationEnabled(state, {
                environmentService,
                featureFlagService,
            });

            if (isRecaptchaEnabled) {
                throw e;
            } else {
                loggerService.log('recaptcha-execute-failed', 'Recaptcha execute failed', {
                    isRecaptchaEnabled: false,
                    error: e,
                });
            }
        }

        const requestBody = {
            configurationId,
            hotelId,
            languageCode,
            reservations: finalReservations,
            promotedServiceReservations: finalPromotedServiceReservations,
            availabilityBlockId: validAvailabilityBlockId,
            customer: {
                email: getCustomerEmail(state),
                firstName: getCustomerFirstName(state),
                lastName: getCustomerLastName(state),
                telephone: getCustomerPhone(state),
                addressLine1: getCustomerAddress(state),
                city: getCustomerCity(state),
                postalCode: getCustomerPostalCode(state),
                nationalityCode: getCustomerNationalityCode(state),
                sendMarketingEmails: bookerEnabled ? false : getSendMarketingEmails(state),
            },
            creditCardData,
            recaptchaVerificationToken,
        };

        if (bookerEnabled) {
            (requestBody as $TSFixMe).booker = {
                email: getBookerEmail(state),
                firstName: getBookerFirstName(state),
                lastName: getBookerLastName(state),
                telephone: getBookerPhone(state),
                sendMarketingEmails: getSendMarketingEmails(state),
            };
        }

        return apiService
            .createReservationGroup(requestBody)
            .then((response: ReservationGroupResult) => {
                dispatch(createAction(CREATE_RESERVATION_GROUP_SUCCEEDED, { ...response }));
                const reservationGroupId = response.reservationGroups[0]?.id;
                const customerId = response.reservationGroups[0]?.customerId;

                gainsightService.setGlobalContext({
                    customerId,
                    reservationGroupId,
                    reservationsIds: response.reservations.map((reservation) => reservation.id),
                });

                return response;
            })
            .catch((error: $TSFixMe) => {
                const exceedingReservationIndexes = error?.data?.ExceedingReservationIndexes;
                if (exceedingReservationIndexes != null) {
                    if (serviceId == null) {
                        dispatch(
                            logSentryError({
                                error: new TypeError(
                                    'Cannot update availability data with exceeding reservation counts. ServiceId is nil.'
                                ),
                                extra: { configurationId, enterpriseId: hotelId, bookerEnabled, availabilityBlockId },
                            })
                        );
                    } else {
                        const exceedingReservationCounts = getExceedingReservationsData({
                            // @ts-expect-error $TSFixMeError: Property '0' is missing in type '{ roomCategoryId: string; startUtc: string; endUtc: string; voucherCode: string | undef...
                            requestedReservations: finalReservations,
                            exceedingReservationIndexes,
                            enterpriseId: hotelId,
                            timezone,
                            serviceId,
                        });
                        const isDayUse = hasEnterpriseDayUseServices(state, { enterpriseId: hotelId });
                        dispatch(updateAvailabilityData(exceedingReservationCounts, isDayUse, timezone));
                    }
                }

                dispatch(createAction(CREATE_RESERVATION_GROUP_FAILED, { error }));
                throw error;
            });
    };
}

export function fetchReservationGroup(): DistributorPromiseThunkAction {
    return async (dispatch, getState, { apiService, gainsightService }) => {
        const state = getState();
        const hotelId = getSelectedHotelId(state);
        const reservationGroupId = getReservationGroupIdFromQuery(state);

        dispatch(createAction(FETCH_RESERVATION_GROUP_STARTED));

        if (reservationGroupId == null) {
            const error = new Error('Confirmation screen - no reservation group ids received in url query.');
            dispatch(createAction(FETCH_RESERVATION_GROUP_FAILED, { error }));
            throw error;
        }

        return apiService
            .getReservationGroup({ hotelId, ids: [reservationGroupId] })
            .then((response) => {
                dispatch(createAction(FETCH_RESERVATION_GROUP_SUCCEEDED, { ...response }));
                const customerId = response.reservationGroups[0]?.customerId;
                const { reservations } = response;

                gainsightService.setGlobalContext({
                    customerId,
                    reservationGroupId,
                    reservationsIds: reservations.map((reservation) => reservation.id),
                });
            })
            .catch((error: $TSFixMe) => {
                dispatch(createAction(FETCH_RESERVATION_GROUP_FAILED, { error }));
                throw error;
            });
    };
}
